import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { carModelAPI, carVariantAPI, BASE_URL } from '../services/api';
import './Brands.css';

const CarModelDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [model, setModel] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeKeyFeatureTab, setActiveKeyFeatureTab] = useState(null);
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);

  // Image carousel state
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const [showLightbox, setShowLightbox] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  useEffect(() => {
    fetchModelDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchModelDetails = async () => {
    try {
      setLoading(true);
      const response = await carModelAPI.getById(id);
      setModel(response.data);

      // Set default key feature tab
      if (response.data.keyFeatures && response.data.keyFeatures.length > 0) {
        const categories = [...new Set(response.data.keyFeatures.map(f => f.category))];
        setActiveKeyFeatureTab(categories[0]);
      }

      // Use variants from model response if available, otherwise fetch separately
      if (response.data.variants && response.data.variants.length > 0) {
        setVariants(response.data.variants);
      } else {
        const variantsResponse = await carVariantAPI.getAll({ modelId: id, limit: 100 });
        setVariants(variantsResponse.data.data || []);
      }

      setError('');
    } catch (error) {
      console.error('Error fetching model details:', error);
      setError('Failed to load model details');
    } finally {
      setLoading(false);
    }
  };

  // Group key features by category
  const getKeyFeaturesByCategory = () => {
    if (!model?.keyFeatures || model.keyFeatures.length === 0) return {};
    return model.keyFeatures.reduce((acc, feature) => {
      if (!acc[feature.category]) acc[feature.category] = [];
      acc[feature.category].push(feature);
      return acc;
    }, {});
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${BASE_URL}${imagePath}`;
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    if (typeof price === 'string') {
      if (price.includes('Lakh') || price.includes('Crore') || price.includes('Rs')) {
        return price;
      }
      const numPrice = parseFloat(price.replace(/[^0-9.]/g, ''));
      if (!isNaN(numPrice) && numPrice > 0) {
        if (numPrice >= 10000000) {
          return `Rs. ${(numPrice / 10000000).toFixed(2)} Crore`;
        } else if (numPrice >= 100000) {
          return `Rs. ${(numPrice / 100000).toFixed(2)} Lakh`;
        }
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(numPrice);
      }
      return price;
    }
    return '-';
  };

  // Gallery carousel navigation
  const nextImage = useCallback(() => {
    if (model?.images && model.images.length > 0) {
      setCurrentImageIdx(prev => (prev + 1) % model.images.length);
    }
  }, [model?.images]);

  const prevImage = useCallback(() => {
    if (model?.images && model.images.length > 0) {
      setCurrentImageIdx(prev => (prev - 1 + model.images.length) % model.images.length);
    }
  }, [model?.images]);

  // Touch handlers for swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) nextImage();
    else if (distance < -minSwipeDistance) prevImage();
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showLightbox) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') setShowLightbox(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox, nextImage, prevImage]);

  if (loading) {
    return (
      <div className="brands-page">
        <div className="loading">Loading model details...</div>
      </div>
    );
  }

  if (error || !model) {
    return (
      <div className="brands-page">
        <div className="alert alert-error">{error || 'Model not found'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/car-models')}>
          Back to Models
        </button>
      </div>
    );
  }

  const colors = model.colors || [];
  const galleryImages = model.images || [];

  return (
    <div className="brands-page">
      {/* Gallery Lightbox Modal */}
      {showLightbox && galleryImages.length > 0 && (
        <div className="lightbox-overlay" onClick={() => setShowLightbox(false)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setShowLightbox(false)}>&times;</button>
            <button className="lightbox-nav lightbox-prev" onClick={prevImage}>&#10094;</button>
            <div
              className="lightbox-image-container"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {galleryImages[currentImageIdx] && (
                <img
                  src={getImageUrl(galleryImages[currentImageIdx].image)}
                  alt={galleryImages[currentImageIdx].alt || `${model.name} image`}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
            </div>
            <button className="lightbox-nav lightbox-next" onClick={nextImage}>&#10095;</button>
            <div className="lightbox-caption">
              <span>{galleryImages[currentImageIdx]?.alt || `${model.name} - Image ${currentImageIdx + 1}`}</span>
              <span className="lightbox-counter">{currentImageIdx + 1} / {galleryImages.length}</span>
            </div>
          </div>
        </div>
      )}

      <div className="page-header">
        <div className="breadcrumb">
          <button className="btn btn-link" onClick={() => navigate('/car-models')}>
            Car Models
          </button>
          <span className="breadcrumb-separator">/</span>
          <span>{model.name}</span>
        </div>
      </div>

      <div className="details-container">
        {/* Main Info Card */}
        <div className="details-card details-main">
          <div className="details-image-section">
            <div className="details-image">
              {(colors[selectedColorIdx]?.image || model.image) ? (
                <img
                  src={getImageUrl(colors[selectedColorIdx]?.image || model.image)}
                  alt={model.name}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="no-image-placeholder large">No Image Available</div>
              )}
            </div>

            {/* Color Swatches */}
            {colors.length > 0 && (
              <div className="details-colors">
                <h4>Available Colors ({colors.length})</h4>
                <div className="color-swatches-large">
                  {colors.map((color, idx) => {
                    const isSelected = selectedColorIdx === idx;
                    let colorHex = '#888';
                    if (color.hexCode) {
                      colorHex = color.hexCode.startsWith('#') ? color.hexCode : `#${color.hexCode}`;
                    }
                    return (
                      <div
                        key={idx}
                        className={`color-swatch-large ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedColorIdx(idx)}
                      >
                        <div className="color-circle" style={{ backgroundColor: colorHex }} />
                        <span className="color-name">{color.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="details-info-section">
            <div className="details-header">
              <h1>{model.name}</h1>
              <p className="details-subtitle">{model.brand?.brandName}</p>
            </div>

            <div className="details-price-section">
              <div className="price-box">
                <span className="price-label">Price Range</span>
                <span className="price-value primary">{model.priceRange || '-'}</span>
              </div>
            </div>

            <div className="details-quick-specs">
              {model.fuelType && (
                <div className="quick-spec">
                  <span className="spec-icon">&#9981;</span>
                  <span>{model.fuelType}</span>
                </div>
              )}
              {model.transmissionType && (
                <div className="quick-spec">
                  <span className="spec-icon">&#9881;</span>
                  <span>{model.transmissionType}</span>
                </div>
              )}
              {model.engine && (
                <div className="quick-spec">
                  <span className="spec-icon">&#9889;</span>
                  <span>{model.engine}</span>
                </div>
              )}
              {model.mileage && (
                <div className="quick-spec">
                  <span className="spec-icon">&#128663;</span>
                  <span>{model.mileage}</span>
                </div>
              )}
              {model.seatingCapacity && (
                <div className="quick-spec">
                  <span className="spec-icon">&#128101;</span>
                  <span>{model.seatingCapacity}</span>
                </div>
              )}
            </div>

            {/* Ratings */}
            {(model.avgRating || model.reviewCount || model.safetyRating) && (
              <div className="details-ratings">
                {model.avgRating && (
                  <div className="rating-box">
                    <span className="rating-value">{model.avgRating}</span>
                    <span className="rating-label">Avg Rating</span>
                  </div>
                )}
                {model.reviewCount && (
                  <div className="rating-box">
                    <span className="rating-value">{model.reviewCount}</span>
                    <span className="rating-label">Reviews</span>
                  </div>
                )}
                {model.safetyRating && (
                  <div className="rating-box">
                    <span className="rating-value">{model.safetyRating}</span>
                    <span className="rating-label">Safety</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Key Features Card - from API */}
        {(() => {
          const keyFeaturesByCategory = getKeyFeaturesByCategory();
          const keyFeatureCategories = Object.keys(keyFeaturesByCategory);

          return keyFeatureCategories.length > 0 ? (
            <div className="details-card">
              <h3>Key Specifications & Features</h3>
              <div className="category-tabs">
                {keyFeatureCategories.map(category => (
                  <button
                    key={category}
                    className={`category-tab ${activeKeyFeatureTab === category ? 'active' : ''}`}
                    onClick={() => setActiveKeyFeatureTab(category)}
                  >
                    {category}
                  </button>
                ))}
              </div>
              <div className="specs-grid">
                {activeKeyFeatureTab && keyFeaturesByCategory[activeKeyFeatureTab]?.map((feature, idx) => (
                  <div key={idx} className="spec-row">
                    <span className="spec-label">{feature.featureName}</span>
                    <span className="spec-value">{feature.featureValue || '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}

        {/* Fallback Specifications Card - if no key features from API */}
        {(!model.keyFeatures || model.keyFeatures.length === 0) && (
          <div className="details-card">
            <h3>Specifications</h3>
            <div className="specs-grid">
              <div className="spec-row">
                <span className="spec-label">Engine</span>
                <span className="spec-value">{model.engine || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Power</span>
                <span className="spec-value">{model.power || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Mileage</span>
                <span className="spec-value">{model.mileage || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Fuel Type</span>
                <span className="spec-value">{model.fuelType || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Transmission</span>
                <span className="spec-value">{model.transmissionType || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Seating Capacity</span>
                <span className="spec-value">{model.seatingCapacity || '-'}</span>
              </div>
              <div className="spec-row">
                <span className="spec-label">Air Bags</span>
                <span className="spec-value">{model.airBags || '-'}</span>
              </div>
            </div>
          </div>
        )}

        {/* Variants Card */}
        {variants.length > 0 && (
          <div className="details-card">
            <h3>Variants ({variants.length})</h3>
            <div className="variants-list">
              {variants.map((variant) => (
                <div
                  key={variant.id}
                  className="variant-list-item"
                  onClick={() => navigate(`/car-variants/${variant.id}`)}
                >
                  <div className="variant-list-image">
                    {variant.image ? (
                      <img
                        src={getImageUrl(variant.image)}
                        alt={variant.variantName}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="variant-placeholder">{variant.variantName?.charAt(0) || 'V'}</div>
                    )}
                  </div>
                  <div className="variant-list-info">
                    <h4>{variant.variantName}</h4>
                    <div className="variant-list-specs">
                      {variant.fuelType && <span>{variant.fuelType}</span>}
                      {variant.transmissionType && <span>{variant.transmissionType}</span>}
                      {variant.engine && <span>{variant.engine}</span>}
                    </div>
                  </div>
                  <div className="variant-list-price">
                    {formatPrice(variant.exShowroomPrice || variant.price)}
                  </div>
                  <div className="variant-list-arrow">&#8250;</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Gallery Card with Carousel */}
        {galleryImages.length > 0 && (
          <div className="details-card">
            <h3>Gallery ({galleryImages.length})</h3>
            <div className="gallery-carousel-container">
              {/* Main Gallery Image */}
              <div
                className="gallery-carousel"
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <div className="gallery-main-image" onClick={() => setShowLightbox(true)}>
                  <img
                    src={getImageUrl(galleryImages[currentImageIdx]?.image)}
                    alt={galleryImages[currentImageIdx]?.alt || `${model.name} image`}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="gallery-expand-icon">&#x26F6;</div>
                </div>

                {/* Navigation Arrows */}
                {galleryImages.length > 1 && (
                  <>
                    <button className="gallery-nav gallery-prev" onClick={prevImage}>&#10094;</button>
                    <button className="gallery-nav gallery-next" onClick={nextImage}>&#10095;</button>
                  </>
                )}

                {/* Image Counter */}
                <div className="gallery-counter">
                  {currentImageIdx + 1} / {galleryImages.length}
                </div>
              </div>

              {/* Thumbnail Strip */}
              <div className="gallery-thumbnails">
                {galleryImages.map((img, idx) => (
                  <div
                    key={idx}
                    className={`gallery-thumb ${currentImageIdx === idx ? 'active' : ''}`}
                    onClick={() => setCurrentImageIdx(idx)}
                  >
                    <img
                      src={getImageUrl(img.image)}
                      alt={img.alt || `Thumbnail ${idx + 1}`}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Additional Info Card */}
        <div className="details-card">
          <h3>Additional Information</h3>
          <div className="specs-grid">
            <div className="spec-row">
              <span className="spec-label">Model ID</span>
              <span className="spec-value">{model.modelId || model.id}</span>
            </div>
            <div className="spec-row">
              <span className="spec-label">Slug</span>
              <span className="spec-value">{model.slug}</span>
            </div>
            {model.modelUrl && (
              <div className="spec-row">
                <span className="spec-label">Source URL</span>
                <span className="spec-value">
                  <a href={`https://www.cardekho.com${model.modelUrl}`} target="_blank" rel="noopener noreferrer">
                    View on CardDekho
                  </a>
                </span>
              </div>
            )}
            <div className="spec-row">
              <span className="spec-label">Created At</span>
              <span className="spec-value">{new Date(model.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="spec-row">
              <span className="spec-label">Updated At</span>
              <span className="spec-value">{new Date(model.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarModelDetails;
